import { useEffect, useRef, useCallback } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { store } from "../../../store";

const WS_URL =
  process.env.REACT_APP_GANDALF_WS_URL
    ? `${process.env.REACT_APP_GANDALF_WS_URL}/ws/admin`
    : "ws://localhost:80/ws/admin";

function getCustomerUuid() {
  const { loginReducer } = store.getState();
  return loginReducer?.groups?.[0];
}

/**
 * WebSocket hook for crawl progress notifications.
 *
 * @param {Object} callbacks
 * @param {function} callbacks.onProgress     - (payload) => void   — PROGRESS messages
 * @param {function} callbacks.onUrlStarted   - (payload) => void   — URL_STARTED messages
 * @param {function} callbacks.onUrlFinished  - (payload) => void   — URL_FINISHED messages
 */
export default function useCrawlNotifications({ onProgress, onUrlStarted, onUrlFinished } = {}) {
  const clientRef = useRef(null);
  const callbacksRef = useRef({ onProgress, onUrlStarted, onUrlFinished });

  // Keep callbacks ref current without re-subscribing
  useEffect(() => {
    callbacksRef.current = { onProgress, onUrlStarted, onUrlFinished };
  }, [onProgress, onUrlStarted, onUrlFinished]);

  useEffect(() => {
    const customerUuid = getCustomerUuid();
    if (!customerUuid) return;

    const topic = `/topic/admin/${customerUuid}/crawl`;

    const client = new Client({
      webSocketFactory: () => new SockJS(WS_URL),
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      debug: () => {}, // silent
    });

    client.onConnect = () => {
      client.subscribe(topic, (frame) => {
        let payload;
        try {
          payload = JSON.parse(frame.body);
        } catch (e) {
          console.warn("useCrawlNotifications: failed to parse message", e);
          return;
        }

        const { messageType } = payload;
        const cb = callbacksRef.current;

        if (messageType === "PROGRESS" && cb.onProgress) {
          cb.onProgress(payload);
        } else if (messageType === "URL_STARTED" && cb.onUrlStarted) {
          cb.onUrlStarted(payload);
        } else if (messageType === "URL_FINISHED" && cb.onUrlFinished) {
          cb.onUrlFinished(payload);
        }
      });
    };

    client.onStompError = (frame) => {
      console.warn("useCrawlNotifications: STOMP error", frame.headers?.message);
    };

    client.activate();
    clientRef.current = client;

    return () => {
      if (clientRef.current) {
        clientRef.current.deactivate();
        clientRef.current = null;
      }
    };
  }, []); // connect once on mount
}
